import { useCallback, useEffect, useRef, useState } from "react";
import type { ShiftFormActor } from "../../../shared/shift-production-form/contracts";
import {
  getShiftFormSession,
  loginShiftFormApprover,
  loginShiftFormOperator,
  logoutShiftFormApprover,
  logoutShiftFormOperator,
  recordShiftFormApproverActivity,
  recordShiftFormHumanActivity,
} from "./shift-production-form-gateway";

type SessionState =
  | { phase: "loading" }
  | {
      phase: "ready";
      actor: ShiftFormActor | null;
      expiresAt: string | null;
      idleExpiresAt: string | null;
    }
  | { phase: "error"; message: string };

export function useShiftProductionFormWorkspace() {
  const [session, setSession] = useState<SessionState>({ phase: "loading" });
  const [logoutBusy, setLogoutBusy] = useState(false);
  const lastActivitySentAt = useRef(0);
  const sessionGeneration = useRef(0);

  const reload = useCallback(async () => {
    const generation = ++sessionGeneration.current;
    setSession({ phase: "loading" });
    try {
      const response = await getShiftFormSession();
      if (generation === sessionGeneration.current)
        setSession({ phase: "ready", ...response });
    } catch (error) {
      if (generation === sessionGeneration.current)
        setSession({
          phase: "error",
          message:
            error instanceof Error ? error.message : "Oturum doğrulanamadı.",
        });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (session.phase !== "ready" || !session.actor || session.actor.kind === "CORPORATE") return;
    const generation = sessionGeneration.current;
    const expiry = Math.min(
      new Date(session.expiresAt ?? 0).getTime(),
      new Date(session.idleExpiresAt ?? 0).getTime(),
    );
    const timeout = window.setTimeout(
      () => {
        if (generation !== sessionGeneration.current) return;
        sessionGeneration.current += 1;
        setSession({
          phase: "ready",
          actor: null,
          expiresAt: null,
          idleExpiresAt: null,
        });
      },
      Math.max(0, expiry - Date.now()),
    );
    return () => window.clearTimeout(timeout);
  }, [session]);

  useEffect(() => {
    if (session.phase !== "ready" || !session.actor || session.actor.kind === "CORPORATE") return;
    const activity = () => {
      if (Date.now() - lastActivitySentAt.current < 4 * 60_000) return;
      lastActivitySentAt.current = Date.now();
      const generation = sessionGeneration.current;
      const activityRequest = session.actor?.kind === "APPROVER"
        ? recordShiftFormApproverActivity
        : recordShiftFormHumanActivity;
      void activityRequest()
        .then((response) =>
          setSession((current) => {
            if (
              generation !== sessionGeneration.current ||
              current.phase !== "ready" ||
              !current.actor || current.actor.kind === "CORPORATE" ||
              response.actor?.id !== current.actor.id
            )
              return current;
            return { phase: "ready", ...response };
          }),
        )
        .catch(() => {
          if (generation !== sessionGeneration.current) return;
          sessionGeneration.current += 1;
          setSession({
            phase: "ready",
            actor: null,
            expiresAt: null,
            idleExpiresAt: null,
          });
        });
    };
    window.addEventListener("pointerdown", activity);
    window.addEventListener("keydown", activity);
    window.addEventListener("input", activity);
    return () => {
      window.removeEventListener("pointerdown", activity);
      window.removeEventListener("keydown", activity);
      window.removeEventListener("input", activity);
    };
  }, [session.phase === "ready" ? session.actor?.id : null]);

  const acceptSession = useCallback(
    (response: Awaited<ReturnType<typeof getShiftFormSession>>) => {
      sessionGeneration.current += 1;
      setSession({ phase: "ready", ...response });
    },
    [],
  );

  const verifyOperatorPin = useCallback(
    (pin: string) => loginShiftFormOperator(pin),
    [],
  );

  const verifyApproverPin = useCallback(
    (pin: string) => loginShiftFormApprover(pin),
    [],
  );

  const clearOperatorSession = useCallback(async () => {
    sessionGeneration.current += 1;
    try {
      await logoutShiftFormOperator();
    } finally {
      setSession({
        phase: "ready",
        actor: null,
        expiresAt: null,
        idleExpiresAt: null,
      });
    }
  }, []);

  const clearApproverSession = useCallback(async () => {
    sessionGeneration.current += 1;
    try {
      await logoutShiftFormApprover();
    } finally {
      setSession({ phase: "ready", actor: null, expiresAt: null, idleExpiresAt: null });
    }
  }, []);

  const logout = async () => {
    setLogoutBusy(true);
    try {
      if (session.phase === "ready" && session.actor?.kind === "OPERATOR")
        await logoutShiftFormOperator();
      else if (session.phase === "ready" && session.actor?.kind === "APPROVER")
        await logoutShiftFormApprover();
      else
        window.location.assign("/auth/logout?returnTo=%2Fvardiya-uretim-formu");
      sessionGeneration.current += 1;
      setSession({
        phase: "ready",
        actor: null,
        expiresAt: null,
        idleExpiresAt: null,
      });
    } finally {
      setLogoutBusy(false);
    }
  };

  return {
    session,
    reload,
    logout,
    logoutBusy,
    acceptSession,
    verifyOperatorPin,
    verifyApproverPin,
    clearOperatorSession,
    clearApproverSession,
  };
}
